import { App, Form } from 'antd';
import dayjs from 'dayjs';
import { PaymentTypes } from 'constants';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { v4 as uuid } from 'uuid';
import { getHttpErrorMessage } from 'utils/http';
import { captureException } from 'utils/errors';
import http from 'utils/http';
import AdditionalDetails from './AdditionalDetails';
import DeliveryInfo from './DeliveryInfo';
import EstimatedInfo from './EstimatedInfo';
import GeneralInfo from './GeneralInfo';
import ItemsInfo from './Items';
import PaymentInfo from './PaymentInfo';
import { createDirectEntryOrder, fetchOrderEstimate } from './requests';
import { StepsContext } from './Steps/StepsProvider';

function orderToCreateFormValues(order) {
  if (!order) return null;
  const { merchant, customer, order_items, delivery_fare, adjustment_amount, recipient_name, ...rest } = order;
  const [recipientFirst = '', recipientLast = ''] = (recipient_name || '').trim().split(/\s+(.+)$/);

  function mapOrderItem(item) {
    const baseItem = {
      id: `clone-${item.id}-${uuid()}`,
      qty: item.qty ?? 1,
      message: item.message ?? '',
      real_price: Number(item.real_price || 0),
      real_total_value: Number(item.real_total_value || item.real_price || 0),
    };
    if (item.is_custom_item) {
      return {
        ...baseItem,
        item_related: { name: item.item_name, catering_label: item.unit },
        size: {
          name: 'Default',
          position: 1,
          serving_size: item.serving_size,
          marked_up_price: Number(item.real_price || 0),
          discounted_marked_up_price: Number(item.real_price || 0),
        },
        is_custom_item: true,
      };
    }
    const sizeObj = {
      position: item.size_number ?? 1,
      name: item.size ?? 'Default',
      serving_size: item.serving_size,
      marked_up_price: Number(item.real_price || 0),
      discounted_marked_up_price: Number(item.real_price || 0),
    };
    const orderOptions = (item.order_options || []).map((opt) => {
      const mo = typeof opt.modifier_option === 'object' ? opt.modifier_option : { id: opt.modifier_option, title: '' };
      return { ...opt, modifier_option: mo, qty: opt.qty ?? 1, real_price: Number(opt.real_price || 0) };
    });
    return {
      ...baseItem,
      item_related: item.item_related ?? { id: null, name: item.item_name },
      size: sizeObj,
      order_options: orderOptions,
    };
  }

  return {
    merchant: merchant ? { value: merchant.id, label: merchant.name, id: merchant.id, ...merchant } : undefined,
    customer: customer ? { value: customer.id, label: customer.name, id: customer.id, ...customer } : undefined,
    items: (order_items || []).map(mapOrderItem),
    has_delivery: rest.has_delivery ?? false,
    address: rest.address?.id ?? rest.address,
    scheduled_for: rest.scheduled_for,
    delivery_message: rest.delivery_message,
    headcount: rest.headcount,
    recipient_name: recipientFirst,
    recipient_last_name: recipientLast,
    recipient_phone: rest.recipient_number,
    adjustment: rest.adjustment_amount,
    delivery_fee: delivery_fare,
    coupon_code: rest.coupon?.code ?? '',
  };
}

function OrderForm() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { setLoading } = useContext(StepsContext);
  const [open, setOpen] = useState(false);
  const [estimatedData, setEstimatedData] = useState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneOrderId = searchParams.get('clone');

  useEffect(() => {
    if (!cloneOrderId) return;
    http.get(`v1/orders/${cloneOrderId}/`)
      .then(async (res) => {
        const order = res?.data?.data ?? res?.data;
        let formValues = orderToCreateFormValues(order);
        if (!formValues) return;
        const merchantId = formValues.merchant?.id ?? order?.merchant?.id;
        const scheduledFor = formValues.scheduled_for;
        if (scheduledFor && merchantId) {
          const parsed = dayjs(scheduledFor);
          if (parsed.isValid() && parsed.isBefore(dayjs())) {
            try {
              const { data: { data } } = await http.get(`v1/restaurants/${merchantId}/availability/`);
              if (data?.first_available_time) {
                formValues = { ...formValues, scheduled_for: data.first_available_time };
                message.info('Original time has passed — using first available time');
              } else {
                formValues = { ...formValues, scheduled_for: undefined };
                message.warning('Original time has passed — please select a new time');
              }
            } catch {
              formValues = { ...formValues, scheduled_for: undefined };
              message.warning('Original time has passed — please select a new time');
            }
          }
        }
        requestAnimationFrame(() => {
          form.setFieldsValue(formValues);
        });
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e) || 'Failed to load order for copying');
      });
  }, [cloneOrderId, form, message]);

  function getPaymentData() {
    const order = form.getFieldsValue();
    if (order.payment_method?.type === PaymentTypes.QUOTED) {
      return {
        quote_receiver_email: order.quotedPaymentMeta?.email,
        quote_receiver_phone: order.quotedPaymentMeta?.phone,
        is_quote: true,
        payment_type: order.quotedPaymentMeta?.type || PaymentTypes.CARD,
      };
    }
    if (order.payment_method?.type === PaymentTypes.CUSTOM) {
      return {
        payment_type: order.payment_method?.type,
        custom_payment_id: order.payment_method?.id,
        custom_payment_meta: order.customPaymentMeta,
      };
    }
    if (order.payment_method?.type === PaymentTypes.CARD) {
      return {
        payment_type: order.payment_method.type,
        card_id: order.card_id,
      };
    }
    return {};
  }
  const getOrderPayload = () => {
    const order = form.getFieldsValue();

    const orderItems = order?.items?.map((item) => (item.is_custom_item ? {
      name: item.item_related.name,
      qty: item.qty,
      real_price: item.real_price,
      serving_size: item.size?.serving_size,
      unit: item.item_related?.catering_label,
      size: 'Default',
      message: item.message,
    } : {
      item_id: item.item_related.id,
      qty: item.qty,
      size: item.size.position,
      message: item.message,
      modifiers: item.order_options?.map((b) => b.modifier_option.id),
    }));

    const includeKitchenTip = Boolean(
      order.has_delivery && (
        order.has_kitchen_tip
        || order.kitchen_tip_percentage != null
        || order.kitchen_tip != null
      )
    );

    const kitchenTipPercentageValue = order.kitchen_tip_percentage;
    const isKitchenTipCustomAmount = (
      kitchenTipPercentageValue == null
      || kitchenTipPercentageValue === -1
      || kitchenTipPercentageValue === '-1'
    );
    const kitchenTipAmountValue = order.kitchen_tip;
    const parsedKitchenTipAmount = kitchenTipAmountValue === '' || kitchenTipAmountValue == null
      ? null
      : Number(kitchenTipAmountValue);
    const kitchenTipAmount = Number.isFinite(parsedKitchenTipAmount) ? parsedKitchenTipAmount : null;

    // Handle tip: if has_tip is false or not set, send tip: 0
    // If has_tip is true with custom amount (-1), send the custom tip value
    // If has_tip is true with percentage, send tip_percentage
    const hasTipEnabled = order.has_tip === true;
    const isCustomTipAmount = order.tip_percentage === -1 || order.tip_percentage === '-1';
    
    let tipValue = null;
    let tipPercentageValue = null;
    
    if (!hasTipEnabled) {
      // No tip selected - send 0
      tipValue = 0;
    } else if (isCustomTipAmount) {
      // Custom tip amount
      tipValue = Number(order.tip) || 0;
    } else if (order.tip_percentage != null) {
      // Tip percentage selected
      tipPercentageValue = order.tip_percentage;
    } else {
      // has_tip is true but nothing selected - default to 0
      tipValue = 0;
    }

    return {
      restaurant: order.merchant?.value,
      has_delivery: order.has_delivery,
      address: order.address?.id,
      tip: tipValue,
      tip_percentage: tipPercentageValue,
      kitchen_tip: includeKitchenTip
        ? (isKitchenTipCustomAmount ? kitchenTipAmount : null)
        : null,
      kitchen_tip_percentage: includeKitchenTip
        ? (isKitchenTipCustomAmount ? null : kitchenTipPercentageValue)
        : null,
      coupon_code: order.coupon_code || '',
      pickup_time: null,
      items: orderItems,
      user_id: order.customer?.id,
      adjustment_amount: Number(order.adjustment || 0),
      delivery_fee: Number(order.delivery_fee || 0),
      source: 'Direct Entry',
      order_source: 'Direct Entry', // TODO: remove this
      scheduled_for: order.scheduled_for,
      delivery_message: order.delivery_message,
      headcount: order.headcount,
      recipient_name: order.recipient_name && `${order.recipient_name} ${order.recipient_last_name}`,
      recipient_number: order.recipient_phone,
      ...getPaymentData(),
    };
  };

  const getOrderEstimateData = () => {
    setLoading(true);
    const estimatePayload = getOrderPayload();
    fetchOrderEstimate(estimatePayload).then((res) => {
      if (res?.status === 'success') {
        setEstimatedData(res?.data);
        setOpen(true);
      }
    })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e) || 'Something went wrong during estimate order!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = async (e) => {
    const payload = getOrderPayload();
    setLoading(true);
    return createDirectEntryOrder(payload)
      .then((res) => {
        if (res?.status === 'success' && res?.data) {
          // Navigate to orders list with the created order data
          // The order data is nested under res.data.result
          const orderData = res.data?.result || res.data;          
          navigate('/console/orders', {
            state: {
              newlyCreatedOrder: orderData,
              showOrderNotification: true,
            },
          });
        }
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e) || 'Something went wrong during create order!');
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Form
      form={form}
      layout="vertical"
      onFinishFailed={() => {
        message.error('Please fix errors!');
      }}
      onFinish={getOrderEstimateData}
      initialValues={{ has_delivery: false }}
      validateMessages={{ required: '${label} is required!' }}
    >
      <GeneralInfo />
      <DeliveryInfo />
      <ItemsInfo />
      <PaymentInfo />
      <AdditionalDetails
        onValidateCoupon={async () => {
          try {
            const order = form.getFieldsValue();
            const code = (order.coupon_code || '').trim();
            if (!code) {
              return { success: false, error: 'Please enter a coupon code first.' };
            }
            if (!order.merchant?.value) {
              return { success: false, error: 'Please select a merchant first.' };
            }
            if (!order.customer?.id) {
              return { success: false, error: 'Please select a customer first.' };
            }
            const items = order?.items || [];
            if (!items.length) {
              return { success: false, error: 'Please add at least one item to the order first.' };
            }
            const payload = getOrderPayload();
            const res = await fetchOrderEstimate(payload);
            if (res?.status === 'success' && res?.data) {
              const couponAmount = res.data?.coupon_amount ?? 0;
              return { success: true, couponAmount };
            }
            const errDetail = res?.detail || res?.title || res?.meta?.detail;
            return { success: false, error: errDetail || 'Invalid or expired coupon.' };
          } catch (e) {
            throw e;
          }
        }}
      />

      <EstimatedInfo
        data={estimatedData}
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
      />
    </Form>
  );
}

export default OrderForm;
